import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdvancedSearch from './AdvancedSearch';
import { EyeIcon, DocumentArrowDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const { isAuthenticated } = useAuth();

  // Use useCallback to prevent fetchEmployees from changing on every render
  const fetchEmployees = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams();
      
      // Add search parameters - map AdvancedSearch fields to API parameters
      if (filters.name) queryParams.append('search', filters.name);
      if (filters.employeeId) queryParams.append('employee_id', filters.employeeId);
      if (filters.department) queryParams.append('department', filters.department);
      if (filters.position) queryParams.append('position', filters.position);
      if (filters.location) queryParams.append('location', filters.location);
      if (filters.minSalary) queryParams.append('min_salary', filters.minSalary);
      if (filters.maxSalary) queryParams.append('max_salary', filters.maxSalary);
      if (filters.status) queryParams.append('employment_status', filters.status);
      if (filters.hireStartDate) queryParams.append('hire_date_start', filters.hireStartDate);
      if (filters.hireEndDate) queryParams.append('hire_date_end', filters.hireEndDate);
      if (filters.leaveType) queryParams.append('leave_type', filters.leaveType);
      

      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/employees?${queryParams.toString()}`,
        {
          withCredentials: true
        }
      );

      const data = response.data;
      console.log('API Response:', data); // Debug log

      // Handle different possible response structures
      if (data.employees) {
        setEmployees(data.employees);
        setTotalCount(data.total_count || data.employees.length);
        setCurrentPage(data.current_page || 1);
        setTotalPages(data.total_pages || 1);
      } else if (Array.isArray(data)) {
        // If the response is directly an array
        setEmployees(data);
        setTotalCount(data.length);
        setCurrentPage(1);
        setTotalPages(1);
      } else {
        // If unexpected response structure
        console.error('Unexpected API response structure:', data);
        setEmployees([]);
        setTotalCount(0);
        setCurrentPage(1);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEmployees(activeFilters);
    }
  }, [fetchEmployees, activeFilters, isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showReportDropdown && !event.target.closest('.relative')) {
        setShowReportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReportDropdown]);

  const handleSearch = (searchFilters) => {
    setActiveFilters(searchFilters);
    setCurrentPage(1);
  };

  const handleFilterChange = (searchFilters) => {
    setActiveFilters(searchFilters);
  };

  const generateReport = async (format) => {
    try {
      setShowReportDropdown(false);
      
      const queryParams = new URLSearchParams();
      
      // Add all current filters to the report request
      if (activeFilters.name) queryParams.append('search', activeFilters.name);
      if (activeFilters.employeeId) queryParams.append('employee_id', activeFilters.employeeId);
      if (activeFilters.department) queryParams.append('department', activeFilters.department);
      if (activeFilters.position) queryParams.append('position', activeFilters.position);
      if (activeFilters.location) queryParams.append('location', activeFilters.location);
      if (activeFilters.minSalary) queryParams.append('min_salary', activeFilters.minSalary);
      if (activeFilters.maxSalary) queryParams.append('max_salary', activeFilters.maxSalary);
      if (activeFilters.status) queryParams.append('employment_status', activeFilters.status);
      if (activeFilters.hireStartDate) queryParams.append('hire_date_start', activeFilters.hireStartDate);
      if (activeFilters.hireEndDate) queryParams.append('hire_date_end', activeFilters.hireEndDate);
      if (activeFilters.leaveType) queryParams.append('leave_type', activeFilters.leaveType);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/employees/report/${format}?${queryParams.toString()}`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp and filter info
      const timestamp = new Date().toISOString().split('T')[0];
      const filterCount = Object.values(activeFilters).filter(v => v && v !== '').length;
      const filterSuffix = filterCount > 0 ? `_filtered_${filterCount}` : '';
      
      link.download = `employee_history_report${filterSuffix}_${timestamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating report:', error);
      
      // Handle different types of error responses
      if (error.response?.data?.detail) {
        // JSON error response
        alert(`Report generation failed: ${error.response.data.detail}`);
      } else if (error.response?.data instanceof Blob) {
        // Error message might be in the Blob
        try {
          const text = await error.response.data.text();
          console.log('Blob error content:', text);
          
          // Try to parse as JSON first
          try {
            const errorData = JSON.parse(text);
            alert(`Report generation failed: ${errorData.detail || errorData.message || text}`);
          } catch {
            // Not JSON, show as text
            alert(`Report generation failed: ${text.substring(0, 200)}...`);
          }
        } catch (blobError) {
          console.error('Error reading blob:', blobError);
          alert(`Report generation failed with server error (Status: ${error.response.status}). Unable to read error details.`);
        }
      } else if (error.response?.status === 500) {
        alert(`Report generation failed with server error. Status: ${error.response.status}. Please check with system administrator.`);
      } else {
        alert('Error generating report. Please try again.');
      }
    }
  };

  if (loading && (!employees || employees.length === 0)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Timeline Investigation</h1>
            <p className="mt-1 text-sm text-gray-500">
              Advanced search and timeline analysis for employee history investigation
            </p>
          </div>
          
          {/* Report Button */}
          <div className="relative">
            <button
              onClick={() => setShowReportDropdown(!showReportDropdown)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Generate Report
              <ChevronDownIcon className="h-4 w-4 ml-2" />
            </button>
            
            {/* Dropdown Menu */}
            {showReportDropdown && (
              <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="py-1">
                  <button
                    onClick={() => generateReport('csv')}
                    className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Export as CSV
                    <span className="ml-auto text-xs text-gray-500">Spreadsheet data</span>
                  </button>
                  <button
                    onClick={() => generateReport('pdf')}
                    className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Export as PDF
                    <span className="ml-auto text-xs text-gray-500">Professional report</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Search Component */}
      <AdvancedSearch 
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        filters={activeFilters}
      />

      {/* Results */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Timeline Investigation Results ({totalCount} employees)
            </h3>
            {totalCount > 0 && (
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="spinner"></div>
            </div>
          ) : (!employees || employees.length === 0) ? (
            <div className="text-center py-12">
              <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position & Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employment Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employees && employees.map((employee) => {
                      
                      return (
                        <tr 
                          key={employee.id} 
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {employee.employee_id}
                                </div>
                                {employee.email && (
                                  <div className="text-xs text-gray-400">
                                    {employee.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {employee.position?.title || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {employee.position?.department || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {employee.location?.name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              Hired: {new Date(employee.hire_date).toLocaleDateString()}
                            </div>
                            {employee.termination_date && (
                              <div className="text-sm text-red-600">
                                Terminated: {new Date(employee.termination_date).toLocaleDateString()}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              {employee.position?.level || 'Standard'} Level
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              employee.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {employee.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                              to={`/employees/${employee.id}`}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Details
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} employees
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="inline-flex items-center px-3 py-2 text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeList; 